//#region types
import { AuthEnums } from "models/enums";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
//#endregion
import useAPIClientHelper from "./useAPIClientHelper";
import { LocalizationHelperInstance, CurrencyHelper, EnumHelper, UtilsHelper } from "utils/helpers";

function useSearchService() {
  const { apiClientInstance } = useAPIClientHelper();

  const currency = (key: string): Promise<AutocompleteListItem[]> => {
    return new Promise((resolve) => {
      const items = CurrencyHelper.getCurrencyCodeList()
        .filter((code) => code.startsWith(key.toUpperCase()))
        .map((key, index) => ({
          id: key,
          value: key,
          description: LocalizationHelperInstance.getFormattedMessage(key),
          group: "",
        }));

      const suggestions = key
        ? []
        : CurrencyHelper.getSuggestedCurrencyList().map((key, index) => ({
            id: key,
            value: key,
            description: LocalizationHelperInstance.getFormattedMessage(key),
            group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
          }));
      resolve([...suggestions, ...items]);
    });
  };
  const paymentTerms = (key: string): Promise<AutocompleteListItem[]> => {
    return new Promise((resolve) => {
      const items = InvoiceConstants.PaymentTerms.filter((code) => code.toString().startsWith(key.toString())).map(
        (item) => ({
          id: item,
          value: item.toString(),
          group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
        })
      );

      resolve(items);
    });
  };
  const period = (key: string): Promise<AutocompleteListItem[]> => {
    return new Promise((resolve) => {
      const items = InvoiceConstants.Period.filter((code) => code.toString().startsWith(key.toString())).map(
        (item) => ({
          id: item,
          value: item.toString(),
          group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
        })
      );

      resolve(items);
    });
  };
  const convertToAutoCompleteItems = (
    items: WintUtilsWebApi.SearchResult[] | null,
    group?: string
  ): AutocompleteListItem[] => {
    return items
      ? items.map((item) => ({
          id: item.id ?? 0,
          value: item.name ?? "",
          description: item.description,
          group: group ?? "",
        }))
      : [];
  };
  const vatTypes = (key: string): Promise<AutocompleteListItem[]> => {
    return new Promise((resolve) => {
      const items = InvoiceConstants.VatTypes.filter((code) => code.toString().startsWith(key.toString())).map(
        (item) => ({
          id: item,
          value:
            item === 0 ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree) : `${item}%`,
          group: "",
        })
      );

      resolve(items);
    });
  };
  const salaryDeviationVehicleTypes = (): Promise<AutocompleteListItem[]> => {
    return new Promise((resolve) => {
      const items = EnumHelper.getNumericItemValues(WintSuperkollAPIModel.SalaryDeviationVehicleType).map((item) => ({
        id: item,
        value: LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.CarType_${item}`),
      }));

      resolve(items);
    });
  };
  const get = async (searchString: string) => {
    if (UtilsHelper.isEmpty(searchString)) return [];
    else {
      const result = await apiClientInstance(AuthEnums.AuthType.Company).SearchClient.Get({
        searchStr: searchString,
      });

      return result.data.map(
        (item): AutocompleteListItem => ({
          id: item.entityId,
          value: getPrefixTextByType(item.type, item.entityName, item.description) ?? "",
          group: item.type,
          icon: getIconByType(item.type),
          data: item,
        })
      );
    }
  };
  const getIconByType = (type: WintSuperkollAPIModel.SearchEntityType) => {
    switch (type) {
      case WintSuperkollAPIModel.SearchEntityType.Article:
        return IconDefinitionConstants.WBox;
      case WintSuperkollAPIModel.SearchEntityType.Customer:
        return IconDefinitionConstants.WCustomer;
      case WintSuperkollAPIModel.SearchEntityType.CustomerInvoices:
        return IconDefinitionConstants.WPaperPlane;
      case WintSuperkollAPIModel.SearchEntityType.Employee:
        return IconDefinitionConstants.WPerson;
      case WintSuperkollAPIModel.SearchEntityType.IncomingInvoices:
        return IconDefinitionConstants.WInvoiceSupplier;
      case WintSuperkollAPIModel.SearchEntityType.Receipts:
        return IconDefinitionConstants.WReceipt;
      case WintSuperkollAPIModel.SearchEntityType.Supplier:
        return IconDefinitionConstants.WSupplier;
      default:
        return IconDefinitionConstants.WTransactions;
    }
  };
  const getUrlByType = (type: WintSuperkollAPIModel.SearchEntityType, id: any) => {
    switch (type) {
      case WintSuperkollAPIModel.SearchEntityType.Article:
        return `/Article/${id}`;
      case WintSuperkollAPIModel.SearchEntityType.Customer:
        return `/Customer/${id}`;
      case WintSuperkollAPIModel.SearchEntityType.CustomerInvoices:
        return `/Invoice/${id}`;
      case WintSuperkollAPIModel.SearchEntityType.Employee:
        return `/Employee/${id}`;
      case WintSuperkollAPIModel.SearchEntityType.IncomingInvoices:
        return `/IncomingInvoice/${id}`;
      case WintSuperkollAPIModel.SearchEntityType.Receipts:
        return `/Receipt/${id}`;
      case WintSuperkollAPIModel.SearchEntityType.Supplier:
        return `/Supplier/${id}`;
    }
  };
  const getGroupUrlByType = (type: WintSuperkollAPIModel.SearchEntityType) => {
    switch (type) {
      case WintSuperkollAPIModel.SearchEntityType.Article:
        return `/Reports/Articles`;
      case WintSuperkollAPIModel.SearchEntityType.Customer:
        return `/Reports/Customers`;
      case WintSuperkollAPIModel.SearchEntityType.CustomerInvoices:
        return `/Reports/Revenue/Invoices`;
      case WintSuperkollAPIModel.SearchEntityType.Employee:
        return `/Reports/Employees`;
      case WintSuperkollAPIModel.SearchEntityType.IncomingInvoices:
        return `/Reports/Expenses/IncomingInvoices`;
      case WintSuperkollAPIModel.SearchEntityType.Receipts:
        return `/Reports/Expenses/Receipts`;
      case WintSuperkollAPIModel.SearchEntityType.Supplier:
        return `/Reports/Suppliers`;
    }
  };
  const getPrefixTextByType = (type: WintSuperkollAPIModel.SearchEntityType, title: any, subText?: any) => {
    switch (type) {
      case WintSuperkollAPIModel.SearchEntityType.CustomerInvoices:
        return `${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Common.Invoice2
        )} #${subText} ${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Common.To
        ).toLocaleLowerCase()} ${title}`;
      case WintSuperkollAPIModel.SearchEntityType.IncomingInvoices:
        return `${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Common.Invoice2
        )} #${subText} ${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Common.From
        ).toLocaleLowerCase()} ${title}`;
      case WintSuperkollAPIModel.SearchEntityType.Receipts:
        return `${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Receipt.Receipt
        )} #${subText} ${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Common.From
        ).toLocaleLowerCase()} ${title}`;
      default:
        return title;
    }
  };

  return {
    currency,
    paymentTerms,
    period,
    convertToAutoCompleteItems,
    vatTypes,
    salaryDeviationVehicleTypes,
    get,
    getIconByType,
    getUrlByType,
    getGroupUrlByType,
    getPrefixTextByType,
  };
}

export default useSearchService;
